import * as Sentry from "@sentry/nextjs";
import { REFUSAL_MESSAGES, PASS_MESSAGES, RestrictedSourceTypes, UsableMediaTypes } from './constants';
import { getAccessToken } from "@/services/authService";
import {
  MediaObject,
  Source,
  SourceObject,
  TimezoneDetails,
  SimpleSource,
  SOURCE_TYPES,
  MediaType,
  Slide,
  SourceInfo,
} from './types';
import { Time, CalendarDate } from "@internationalized/date";
import { FileTypes, META_FILE_REGEX } from './constants';
import { PressEvent } from "@heroui/react";
import SparkMD5 from 'spark-md5';
import { EventFromChat } from '@/components/answer-item';
import deepEqual from 'deep-equal';
import extractDate from 'extract-date';
import extractTime from 'extract-time';
import { jwtDecode } from 'jwt-decode';
import { trackEvent as trackIntercomEvent } from '@intercom/messenger-js-sdk';

interface TimeParts {
  hoursNumber: number | undefined;
  minutesNumber: number | undefined;
  secondsNumber: number | undefined;
}

const US_TIMEZONES_PLUS_UTC = [
  { name: "America/New_York", abbreviation: "Eastern Time", offset: "GMT-5" , offsetInMilliseconds: -18000000 },
  { name: "America/Chicago", abbreviation: "Central Time", offset: "GMT-6" , offsetInMilliseconds: -21600000 },
  { name: "America/Denver", abbreviation: "Mountain Time", offset: "GMT-7" , offsetInMilliseconds: -25200000 },
  { name: "America/Los_Angeles", abbreviation: "Pacific Time", offset: "GMT-8" , offsetInMilliseconds: -28800000 },
  { name: "America/Anchorage", abbreviation: "Alaska Time", offset: "GMT-9" , offsetInMilliseconds: -32400000 },
  { name: "Pacific/Honolulu", abbreviation: "Hawaii-Aleutian Time", offset: "GMT-10" , offsetInMilliseconds: -36000000 },
  { name: "UTC", abbreviation: "UTC", offset: "GMT" , offsetInMilliseconds: 0 },
];

// This is somewhat brittle as it will always match against any file saved as records.html or preservation-[number].html
// but that's a risk we're already taking and are okay with it for now. Meta here refers to a file from Meta (aka Facebook) not
// and actual metadata file.
export const isMetaBundleFilename = (url: string | undefined): boolean => {
  if (!url) return false;
  const base = url.split(/[#?]/)[0].split('/').pop() || '';
  return META_FILE_REGEX.test(base);
};

export const isMetaFile = (mediaItem?: MediaObject) => {
  if (!mediaItem) return false;
  // Include `markdown`: TAI preview .md threads are typed as markdown when preview content-type is mapped (#775).
  const isTextHtmlOrMarkdown =
    mediaItem.mediaType === 'text' ||
    mediaItem.mediaType === 'html' ||
    mediaItem.mediaType === 'markdown';
  const sourceOrMediaUrl = mediaItem.sourceUrl || mediaItem.mediaUrl;
  return isTextHtmlOrMarkdown && isMetaBundleFilename(sourceOrMediaUrl);
};

/**
 * Returns a stable key for the underlying file URL loaded (preview vs source, XML-derived PDF, etc.).
 * Used by PdfViewer and MediaDisplay to reset state when the displayed asset changes in previews.
 * Must align with the URL actually fetched — not `originalUrl` alone as that may change due to preview generation.
 * 
 * This needs to be in shared functions because it is used in both the PdfViewer and MediaDisplay components and
 * we have to avoid circular dependencies.
 */
export const getMediaObjectUrlKey = (media: MediaObject | undefined): string => {
  if (!media) return '';
  // Use || so '' does not block the next field (mediaDisplay may clear mediaSrc while mediaUrl is set).
  const explicit = media.srcToUse || media.mediaSrc || media.mediaUrl;
  const previewSide =
    media.sourceInfo?.previewObjectUrl ||
    media.previewFilePath ||
    media.derived_object_url ||
    '';

  if (explicit) {
    // Same mediaUrl can denote different assets (e.g. native PDF vs XML-derived preview). Do not return early on URL alone.
    if (previewSide && previewSide !== explicit) {
      return `${explicit}|${previewSide}`;
    }
    return explicit;
  }
  if (previewSide) return previewSide;
  if (media.sourceFilePath) return media.sourceFilePath;
  return media.originalUrl || media.mediaContext?.previewUrl || '';
};

/**
 * Canonical citation page for a source or media object.
 * Prefer top-level `page` (string from chat/LLM), then `pageNumber`, then nested contexts.
 * SourceCard previously used only `.page` and merged `sourceContext`, which could leave a stale
 * `sourceContext.pageNumber` when switching between PDFs (e.g. still requesting page 185 on a ~100-page doc).
 */
export function parseCitationPageFromSourceLike(
  obj: {
    page?: string | number;
    pageNumber?: number;
    mediaContext?: { pageNumber?: number };
    sourceContext?: { pageNumber?: number };
  } | null | undefined,
): number {
  if (!obj) return 1;
  const raw =
    obj.page ??
    obj.pageNumber ??
    obj.mediaContext?.pageNumber ??
    obj.sourceContext?.pageNumber;
  if (raw === undefined || raw === null || raw === '') return 1;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/** MediaObject from SourceCard includes `page` / `pageNumber` from the spread Source row. */
export function parseCitationPageFromMedia(media: MediaObject | undefined): number {
  if (!media) return 1;
  const m = media as MediaObject & {
    page?: string | number;
    pageNumber?: number;
    sourceContext?: { pageNumber?: number };
  };
  return parseCitationPageFromSourceLike({
    page: m.page,
    pageNumber: m.pageNumber,
    mediaContext: media.mediaContext,
    sourceContext: m.sourceContext,
  });
}

export const decodeCongnitoToken = (jwtToken: string): any => {
  try {
    const parts = jwtToken.split('.');
    if (parts.length !== 3) return null;
    const decoded1 = atob(parts[1]);
    return JSON.parse(decoded1);
  } catch (error) {
    try {
      const decoded2 = jwtDecode(jwtToken);
      return decoded2;
    } catch (error) {
      return null;
    }
  }
};

export const getTokenInfo = (jwtToken: string): any => {
  try {
    const tokenInfo1 = jwtDecode(jwtToken);
    return tokenInfo1;
  } catch (error) {
    try {
      const tokenInfo2 = decodeCongnitoToken(jwtToken);
      return tokenInfo2;
    } catch (error) {
      return null;
    }
  }
}

// Helper function to determine if token is Okta (has payload structure) or Cognito (direct properties)
export const isOktaToken = (tokenInfo: any): boolean => {
  const isOktaFromToken = tokenInfo && typeof tokenInfo.payload === 'object' && tokenInfo.payload !== null;
  const isOktaFromCookie = tokenInfo && !!tokenInfo.amr && (tokenInfo.amr.includes('okta_verify') || tokenInfo.amr.includes('okta'));
  return isOktaFromToken || isOktaFromCookie;
}

const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return null;
};

// Helper functions to extract user information from either Okta or Cognito tokens
export const getUserId = (tokenInfo: any): string => {
  if (!tokenInfo) return '';
  if (isOktaToken(tokenInfo)) {
    return tokenInfo.payload?.sub || tokenInfo.sub || '';
  }
  return tokenInfo.sub ?? '';
}

export const getUserEmail = (tokenInfo: any): string => {
  if (!tokenInfo) return '';
  if (isOktaToken(tokenInfo)) {
    return tokenInfo.payload?.email || tokenInfo.email || '';
  }
  return tokenInfo.email ?? '';
}

// Special helper for email that uses access token for Okta, ID token for Cognito
export const getUserEmailForHeaders = (idTokenInfo: any, accessTokenInfo: any): string => {
  if (!idTokenInfo) return '';
  if (isOktaToken(idTokenInfo)) {
    // For Okta, email comes from accessToken.payload.sub
    return accessTokenInfo?.payload?.sub || accessTokenInfo?.sub || '';
  }
  // For Cognito, email comes from idToken.email
  return idTokenInfo.email ?? '';
}

export const getUsername = (tokenInfo: any): string => {
  if (!tokenInfo) return '';
  if (isOktaToken(tokenInfo)) {
    return tokenInfo.payload?.name || tokenInfo.name || '';
  }
  // For Cognito, try cognito:username first, then username as fallback
  return tokenInfo['cognito:username'] ?? tokenInfo.username ?? '';
}

export const validateUserInfo = (id: any, access: any): void => {
  const email = getUserEmailForHeaders(id, access);
  const username = getUsername(id);
  const userId = getUserId(id);

  if (!email || email === '') {
    throw new Error('No email found in tokens');
  }
  if (!username || username === '') {
    throw new Error('No username found in tokens');
  }
  if (!userId || userId === '') {
    throw new Error('No user ID found in tokens');
  }

  // For Okta tokens, validate email consistency between ID and access tokens
  if (isOktaToken(id) && isOktaToken(access)) {
    const accessUserId = getUserId(access);
    if (email !== accessUserId) {
      throw new Error('Email in idToken does not match sub in accessToken');
    }
  }
}

// Helper for decodeToken results (used in frontend components)
export const getUserEmailFromDecodedToken = (decodedToken: any): string => {
  if (!decodedToken) return '';
  // Check if it's an Okta token structure (has payload)
  if (decodedToken.payload) {
    return decodedToken.payload.email ?? '';
  }
  // Cognito token structure (direct properties)
  return decodedToken.email ?? '';
}

export const getUserGroups = (tokenInfo: any): string => {
  if (!tokenInfo) return 'admin,user';
  if (isOktaToken(tokenInfo)) {
    // For Okta, we may need to check for groups differently - using default for now
    return 'admin,user';
  }
  // For Cognito, join the groups array or use default
  const groups = tokenInfo['cognito:groups'];
  if (Array.isArray(groups) && groups.length > 0) {
    return groups.join(',');
  }
  return 'admin,user';
}

// format time to HH:MM:SS or MM:SS if no hours for Media Player
export const formatTimeIndex = (timeStr: string) => {
  if (!timeStr || timeStr.toString().indexOf(':') !== -1) return timeStr;
  let time = parseFloat(timeStr);
  if (isNaN(time)) return '';
  const hours = Math.floor(time / 3600);
  time = time - (hours * 3600)
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `0:${seconds.toString().padStart(2, '0')}`;
  }
};

export const getSourceInfoFromServer = async (sourceFilePath: string) => {
  try {
    if (!sourceFilePath || sourceFilePath.trim() === '') {
      return null;
    }
    const s3Key = encodeURIComponent(sourceFilePath?.split('/').slice(3).join('/') || '');
    const url = `/api/protected/source-info/${s3Key}`;
    const response = await FetchWithVersionCheck(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const sourceInfo = await response.json();
    return sourceInfo;
  } catch (error) {
    // just use the original source file path with the proxy
    return null;
  }
}

const getMatchingSources = (sources: Source[], date: string) => {
  const matchingSources: SourceObject[] = [];
  sources.forEach((source) => {
    if (source.content) {
      const extractedDates = extractDate(source.content, {
        direction: 'MD',
        locale: 'en',
        timeZone: 'America/New_York',
      });
      if (extractedDates && extractedDates.length > 0) {
        for (const extractedDate of extractedDates) {
          const extractedDateParts = extractedDate.date.split('-');
          const dateParts = date.split('-');
          const extractedDateIn = `${extractedDateParts[1]}-${extractedDateParts[2]}`;
          const dateIn = `${dateParts[1]}-${dateParts[2]}`;
          if (extractedDateIn === dateIn) {
            matchingSources.push(source as SourceObject);
          }
        }
      }
    }
  });
  return matchingSources;
}

export const getFirstStartTime = (transcript: string): string | null => {
  try {
    // Look for the first "start": "X:XX" pattern
    const startTimeMatch = transcript.match(/"start":\s*"(\d+:\d+)"/);
    if (startTimeMatch && startTimeMatch[1]) {
      return startTimeMatch[1];
    }
    return null;
  } catch (error) {
    return null;
  }
}

export const getUiSourceFromChatSource = (source: Source) => {
  const sourceType = extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown';
  const transcriptLocation = source.location;
  const hasTranscript = source.location?.includes('/bkb/slow/transcribe/') || (
    source.location?.includes('/bkb/slow/') &&
    source.location?.includes('/media_processing/') &&
    source.location?.endsWith('transcript.txt') // not .transcript.txt in case the filename is just transcript.txt
  );
  const sourceObject: SourceObject = {
    ...source,
    sourceType: sourceType,
    sourceUrl: source.sourceFilePath,
    sourceContext: source.sourceContext || {},
  };
  if (!hasTranscript && source.page && !isNaN(parseInt(source.page))) {
    sourceObject.pageNumber = parseInt(source.page);
    sourceObject.sourceContext!.pageNumber = parseInt(source.page);
  }
  if (hasTranscript) {
    sourceObject.sourceContext = {
      ...sourceObject.sourceContext,
      transcriptUrl: transcriptLocation
    };
    if (source.content) {
      const firstStartTime = getFirstStartTime(source.content);
      if (firstStartTime) {
        sourceObject.sourceContext.startTime = firstStartTime;
      }
    }
  }

  return sourceObject;
}

export const getBestMediaFromSources = (sources: Source[]): MediaObject[] => {
  try {
    let media: MediaObject | undefined = undefined;
    if (!media) {
      media = sources.find((source: any) => getMediaType(source) === 'image') as unknown as MediaObject;
    }
    if (!media) {
      media = sources.find((source: any) => getMediaType(source) === 'video') as unknown as MediaObject;
    }
    if (!media) {
      media = sources.find((source: any) => getMediaType(source) === 'audio') as unknown as MediaObject;
    }
    if (!media) {
      media = sources.find((source: any) => getMediaType(source) === 'pdf') as unknown as MediaObject;
    }
    if (!media) {
      media = sources.find((source: any) => UsableMediaTypes.includes(getMediaType(source))) as unknown as MediaObject;
    }
    if (media) {
      media.mediaType = getMediaType(media);
      media.mediaContext = { ...(media as any).sourceContext, pageNumber: (media as any).pageNumber || 0 };
      if (media.mediaType === 'pdf') {
        if (media.previewFilePath && media.previewFilePath !== "" && media.previewFilePath !== (media as any).sourceFilePath) {
          media.mediaUrl = media.previewFilePath;
        } else {
          media.mediaUrl = (media as any).sourceUrl;
        }
      } else {
        media.mediaUrl = (media as any).sourceUrl;
      }
      return [ media ] as MediaObject[];
    }
    return [] as MediaObject[];
  } catch (error) {
    return [] as MediaObject[];
  }}

export const parseEvents = (event: EventFromChat) => {
  const monthPrefixes = ['late', 'early', 'mid'];
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const shortMonths = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const lines = event.answerText.split('\n');
  const parsedEvents: Slide[] = [];
  let currentYear = undefined;
  let lastYear = undefined;
  let currentMonth = undefined;
  let lastMonth = undefined;
  let eventMonth = undefined;
  let eventDay = undefined;
  let eventText = undefined;
  let description = '';
  let matchingSources: Source[] = [];
  for (const line of lines) {
    let eventTime = '12:00:00';
    let timeIsValid = false;
    if (line.startsWith('#') || line.startsWith('##') || line.startsWith('###')) {
      const monthAndYearRegex = new RegExp(`(${monthPrefixes.join('|')}|${months.join('|')}|${shortMonths.join('|')})\\s+(\\d{4})`, 'i');
      const monthAndYearMatches = line.match(monthAndYearRegex);
      if (monthAndYearMatches && monthAndYearMatches.length > 0) {
        const month = monthAndYearMatches[1];
        const year = monthAndYearMatches[2];
        currentMonth = monthPrefixes.includes(month.toLowerCase()) ? undefined : month;
        currentYear = year;
        eventMonth = undefined;
        eventDay = undefined;
        eventText = undefined;
      } else {
        currentMonth = undefined;
        currentYear = undefined;
      }
    } else if (line.startsWith('- ')) {
      let extractedDate = undefined;
      const extractedDates = extractDate(line, {
        direction: 'MD',
        locale: 'en',
        timeZone: 'America/New_York',
      });
      if (extractedDates && extractedDates.length > 0) {
        let extractedDate0 = extractedDates[0].date;
        const extractedDateParts = extractedDate0.split('-');
        extractedDate = `${currentYear}-${extractedDateParts[1]}-${extractedDateParts[2]}`;
        const month = extractedDateParts[1];
        const day = extractedDateParts[2];
        let item = line.replace(/<sup>.*?<\/sup>/g, '').replace(/<\/p><p>/g, '').trim().slice(2);
        eventTime = '12:00:00';
        timeIsValid = false;
        const extractedTimes = extractTime(item.replaceAll('(', ' ').replaceAll(')', ' '), 24);
        if (extractedTimes && extractedTimes.length > 0) {
          eventTime = extractedTimes[0].time;
          const timeParts = eventTime.split(':');
          const hours = timeParts[0].length === 1 ? `0${timeParts[0]}` : timeParts[0];
          const minutes = timeParts[1].length === 1 ? `0${timeParts[1]}` : timeParts[1];
          const seconds = timeParts[2] ? timeParts[2].length === 1 ? `0${timeParts[2]}` : timeParts[2] : '00';
          eventTime = `${hours}:${minutes}:${seconds}`;
          timeIsValid = true;
        }
        eventMonth = month;
        eventDay = day;
        eventText = item;
        description = '';
      } else {
        eventMonth = undefined;
        eventDay = undefined;
        eventText = undefined;
        description = '';
      }
      if (extractedDate && event.sources) {
        matchingSources = getMatchingSources(event.sources, extractedDate);
      }
    } else if (line.startsWith('  - ')) {
      description = line.replace(/<sup>.*?<\/sup>/g, '').replace(/<\/p><p>/g, '');
    }

    if (!currentMonth || !currentYear || lastMonth !== currentMonth || lastYear !== currentYear) {
      lastMonth = currentMonth;
      lastYear = currentYear;
      eventMonth = undefined;
      eventDay = undefined;
      eventText = undefined;
      description = '';
    } else if (currentMonth && currentYear && eventDay && eventText) {
      try {
        const eventDate = new Date(`${currentMonth} ${eventDay}, ${currentYear}, 12:00:00`).toISOString().replace(/T.*Z/, `T${eventTime}.000Z`);
        let sources = event.sources.slice(0, 12) as SourceObject[];
        if (matchingSources.length > 0) {
          sources = [...matchingSources].concat(event.sources.slice(0, 12)) ? event.sources.slice(0, 12) as SourceObject[] : [];
        }
        const media = getBestMediaFromSources(sources);
        const newEvent = {
          date: eventDate,
          slideTitle: eventText,
          slideDescription: '',
          isTimeValid: timeIsValid,
          show: true,
          sources: sources as SourceObject[],
          media: media,
        }
        if (!deepEqual(newEvent, parsedEvents[parsedEvents.length - 1])) {
          parsedEvents.push(newEvent);
        }
      } catch (error) {
        // do nothing
      }
    }
    if (description && parsedEvents.length > 0 && parsedEvents[parsedEvents.length - 1] && parsedEvents[parsedEvents.length - 1].slideDescription) {
      parsedEvents[parsedEvents.length - 1].slideDescription += description;
    }
  }

  return parsedEvents;
}

export const buildScopeFilter = (scopes: string[], files: string[], excludedFiles: string[], selectedCase: string, caseField: string) => {
  try {
    const caseFilter = selectedCase === "*" ? undefined : {
      equals: {
        key: caseField,
        value: selectedCase,
      },
    };

    const hasScopes = scopes.length > 0;
    const hasFiles = files.length > 0;
    // if there are included files, then excluded files are not used
    const hasExcludedFiles = !hasFiles && excludedFiles.length > 0;
    let orAllFilters: any[] = [];
    let excludedFilesFilter = null;

    // these are or-able filters if > 1
    if (hasScopes || hasFiles) {
      if (hasScopes) {
        scopes.forEach(scope => {
          const hash = SparkMD5.hash(scope);
          orAllFilters.push({
            listContains: {
              key: 'scope',
              value: hash,
            },
          });
        });
      }
      if (hasFiles) {
        const fileHashes = files.map(file => {
          const hash = SparkMD5.hash(file);
          return hash;
        });
        orAllFilters.push({
          in: {
            key: 'source_url_hash',
            value: fileHashes,
          },
        });
      }
    }

    if (hasExcludedFiles) {
      const fileHashes = excludedFiles.map(file => {
        const hash = SparkMD5.hash(file);
        return hash;
      });
      excludedFilesFilter = {
        notIn: {
          key: 'source_url_hash',
          value: fileHashes,
        },
      };
    }

    const useOrAll = orAllFilters.length > 1;
    const useAndAll = (caseFilter && (hasScopes || hasFiles || hasExcludedFiles))
      || ((hasScopes || hasFiles) && hasExcludedFiles)
      || excludedFilesFilter;

    let filter = undefined;

    try {
      if (useAndAll) {
        filter = { andAll: [] };
      } else if (useOrAll) {
        filter = { orAll: [] };
      } else if (caseFilter) {
        filter = caseFilter;
      }

      if (useAndAll) {
        if (caseFilter) {
          (filter as { andAll: any[] }).andAll.push(caseFilter);
        }
        if (useOrAll) {
          (filter as { andAll: any[] }).andAll.push({ orAll: orAllFilters });
        } else if (orAllFilters.length === 1) {
          (filter as { andAll: any[] }).andAll.push(orAllFilters[0]);
        }
        if (hasExcludedFiles) {
          (filter as { andAll: any[] }).andAll.push(excludedFilesFilter);
        }
      } else if (useOrAll) {
        (filter as { orAll: any[] }).orAll = orAllFilters;
      } else if (orAllFilters.length === 1) {
        filter = orAllFilters[0];
      } else if (hasExcludedFiles) {
        filter = {
          andAll: [excludedFilesFilter],
        };
      } else if (caseFilter) {
        filter = caseFilter;
      }
    } catch (error) {
      console.log('Error building SCOPE filter 1: ', error);
      filter = undefined;
    }
    return filter;
  } catch (error) {
    console.log('Error building SCOPE filter 2: ', error);
    return '';
  }
}

export const getTimezoneList = () => {
  /*
  const allTimezones = Intl.supportedValuesOf('timeZone').map(timeZone => {
    const dtfs = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'shortOffset' });
    const offset = dtfs ? dtfs.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : null;
    const dtf = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'long' });
    const timeZoneAbbreviation = dtf ? dtf.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : null;
    const offsetInMilliseconds = offset ? parseInt(offset.replace(/^GMT/, '')) * 3600000 : undefined;
    return {
      name: timeZone,
      abbreviation: timeZoneAbbreviation,
      offset: offset,
      offsetInMilliseconds: offsetInMilliseconds,
    }
  });
  */
  return US_TIMEZONES_PLUS_UTC;
}

export const getTimezoneListByName = (): Record<string, TimezoneDetails> => {
  const timezoneListByName: Record<string, TimezoneDetails> = {};
  Intl.supportedValuesOf('timeZone').forEach(timeZone => {
    const dtfs = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'shortOffset' });
    const offset = dtfs ? dtfs.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : undefined;
    const dtf = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'long' });
    const timeZoneAbbrivation = dtf ? dtf.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : undefined;
    timezoneListByName[timeZone] = {
      name: timeZone,
      abbreviation: timeZoneAbbrivation,
      offset: offset,
      offsetInMilliseconds: offset ? parseInt(offset.replace(/^GMT/, '')) * 3600000 : undefined,
    }
  });
  return timezoneListByName;
}

export const filterSources = (sources: SourceObject[]): SourceObject[] => {
  return sources.filter((source) => {
    const filename = (source.truncatedFilePath || source.sourceUrl || '').split('/').pop() || 'Unknown';
    return source.sourceUrl && source.sourceUrl.trim() !== "" && filename !== "Unknown";
  });
}

export const filterMedia = (media: MediaObject[]): MediaObject[] => {
  return media.filter((media) => {
    const filename = (media.mediaUrl || media.mediaSrc || '').split('/').pop() || 'Unknown';
    return media.mediaUrl && media.mediaUrl.trim() !== "" && filename !== "Unknown";
  });
}

export const getDateFromDateStr = (dateStr: string | null): CalendarDate | null => {
  if (!dateStr) {
    return null;
  }
  const dateParts = dateStr.split('-');
  const year = parseInt(dateParts[0]);
  const month = parseInt(dateParts[1]);
  const day = parseInt(dateParts[2]);
  return new CalendarDate(year, month, day);
};

// note 0, 0 is MIDNIGHT and AM; and 12, 0 is NOON and PM
export const getTimeFromStr = (timeStr: string | null): TimeParts => {
  const timeParts = timeStr?.split(':');
  let hours = timeParts?.[0];
  const minutes = timeParts?.[1];
  const secondsAndPeriod = timeParts?.[2];
  const seconds = secondsAndPeriod?.split(' ')[0];
  const period = secondsAndPeriod?.split(' ')[1];

  const addedHours = period?.toLowerCase() === 'pm' ? 12 : 0;
  if (period?.toLowerCase() === 'am') {
    if (hours === '12') {
      hours = '0';
    }
  }
  let hoursNumber = hours ? parseInt(hours) : undefined;

  if (hoursNumber !== undefined && hoursNumber < 12) {
    hoursNumber = hoursNumber + addedHours;
  }
  const minutesNumber = minutes ? parseInt(minutes) : undefined;
  const secondsNumber = seconds ? parseInt(seconds) : undefined;
  if (hours === undefined || minutes === undefined || seconds === undefined) {
    return { hoursNumber: undefined, minutesNumber: undefined, secondsNumber: undefined };
  }
  return { hoursNumber, minutesNumber, secondsNumber };
};

export const getClientTimeFromStr = (timeStr: string | null): Time | null => {
  const { hoursNumber, minutesNumber, secondsNumber } = getTimeFromStr(timeStr);
  if (hoursNumber !== undefined && minutesNumber !== undefined && secondsNumber !== undefined) {
    return new Time (hoursNumber, minutesNumber, secondsNumber);
  }
  return null;
}

// Usage Tracking

export const trackEvent = async (eventName: string, eventData: any) => {
  try {
    let errorMessage = undefined;
    let warningMessage = undefined;
    const intercomEventData: any = {
      user_id: eventData.user,
      case_id: eventData.case_id,
      session_id: eventData.session_id,
      model_arn: eventData.model_arn,
    };
    if (eventName === 'error' && eventData.message && eventData.message.trim() !== '') {
      errorMessage = eventData.message;
      intercomEventData.error_message = errorMessage;
    } else if (eventName === 'warning' && eventData.message && eventData.message.trim() !== '') {
      warningMessage = eventData.message;
      intercomEventData.warning_message = warningMessage;
    }
    trackIntercomEvent(eventName, intercomEventData);
    const inhouseEventData: any = {
      user: eventData.user,
      case_id: eventData.case_id,
      session_id: eventData.session_id,
      model_arn: eventData.model_arn || undefined,
      reason: errorMessage || warningMessage || eventData.reason || undefined,
      question: eventData.question || undefined,
      answer: eventData.answer || undefined,
    }
    await fetch('/api/protected/intercom', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_name: eventName,
        intercomEventData: inhouseEventData
      })
    });
  } catch (error) { 
    // do nothing
  }
}

// if the tracking is not finding an id, you can use a className like id-<some name> to try to id it
export function recordStat(
    eventName: string | undefined,
    event: React.MouseEvent | PressEvent,
    statsContext: { userInfo: { userId: string; sessionId: string; caseId: string; query: string } },
    useIntercom: boolean
) {
  let elementId = undefined;
  let eventNameToUse = eventName && eventName.trim() !== '' ? eventName : undefined;
  const target = event?.target as HTMLElement;
  if (!eventNameToUse && target) {
    const dataTestId = (target as any)?.['data-testid'];
    elementId = dataTestId || target.id;
    if (!elementId) {
      // check for className that has the prefix 'id-'
      const className = target.className;
      if (className) {
        const id = className.split(' ').find(c => c.startsWith('id-'));
        if (id) {
          elementId = id.replace('id-', '');
        }
      }
      if (!elementId) {
        const parentWithDataTestId = target.closest('[data-testid]');
        const parentWithId = target.closest('[id]');
        if (parentWithDataTestId) {
          elementId = parentWithDataTestId.getAttribute('data-testid') || 'unknown';
        } else if (parentWithId) {
          elementId = parentWithId.id;
        }
      }
    }
  }
  if (!eventNameToUse) {
    eventNameToUse = elementId || 'unknown';
  }
  if (useIntercom) {
    trackEvent(eventNameToUse || 'unknown', {
      user: statsContext?.userInfo?.userId || 'unknown',
      case_id: statsContext?.userInfo?.caseId || 'unknown',
      session_id: statsContext?.userInfo?.sessionId || 'unknown',
      target_id: elementId || 'unknown',
    });
    console.log('Event data captured');
  } else {
    Sentry.captureMessage(eventNameToUse || 'unknown', {
      level: 'info',
      tags: {
        element: elementId || 'unknown',
        action: 'click'
      },
      extra: {
        page: window.location.pathname,
        userId: statsContext?.userInfo?.userId || 'unknown',
        caseId: statsContext?.userInfo?.caseId || 'unknown',
        sessionId: statsContext?.userInfo?.sessionId || 'unknown',
        query: statsContext?.userInfo?.query || 'unknown'
      }
    });
    console.log('Sentry event captured');
  }
}

export function writeToSentry(message: string, level: 'error' | 'warning', error: Error | undefined = undefined) {
  if (error) {
    Sentry.captureMessage(message, {
      level: level,
      extra: {
        error: error
      }
    });
  } else {
    Sentry.captureMessage(message, {
      level: level
    });
  }
}

export function parseS3Uri(uri: string): { bucket: string; key: string } {
  const s3Prefix = 's3://';
  if (!uri.startsWith(s3Prefix)) {
    throw new Error('Invalid S3 URI: Must start with "s3://"');
  }

  const path = uri.slice(s3Prefix.length);
  const [bucket, ...keyParts] = path.split('/');

  if (!bucket) {
    throw new Error('Invalid S3 URI: No bucket specified');
  }

  const key = keyParts.join('/');

  return { bucket, key };
}

export function IsARefusal(
  response: string,
  refusalMessages: string[] = REFUSAL_MESSAGES,
  passMessages: string[] = PASS_MESSAGES,
  citations: any[] = [] // array of citations or sources
): boolean {
  if (citations.length > 0) {
    return false;
  }
  const lowerCaseResponse = response.toLowerCase();
  const passMatch = passMessages.some(message => lowerCaseResponse.includes(message.toLowerCase()));
  if (passMatch) {
    return false;
  }
  const refusalMatch = refusalMessages.some(message => lowerCaseResponse.includes(message.toLowerCase()));
  return refusalMatch;
}

export function IsNotARefusal(
  response: string,
  refusalMessages: string[] = REFUSAL_MESSAGES,
  passMessages: string[] = PASS_MESSAGES,
  citations: any[] = [] // array of citations or sources
): boolean {
  return !IsARefusal(response, refusalMessages, passMessages, citations);
}

/** Parses the `useIntercom` cookie (set from the LaunchDarkly `useIntercom` flag in main.tsx). */
export function useIntercomFromCookieValue(value: string | undefined | null): boolean {
  if (value == null || value === '') return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === '1';
}

export function CaptureError(errorMessage: string, error: Error | undefined = undefined, useIntercom: boolean) {
  console.error(`${errorMessage}: ${error ? error.message : ''}`);
  if (useIntercom) {
    trackEvent('error', {
      message: errorMessage,
    });
  } else {
    writeToSentry(errorMessage, 'error', error);
  }
}

export function CaptureWarning(warningMessage: string, error: Error | undefined = undefined, useIntercom: boolean = false) {
  console.warn(`${warningMessage}: ${error ? error.message : ''}`);
  if (useIntercom) {
    trackEvent('warning', {
      message: warningMessage,
    });
  } else {
    writeToSentry(warningMessage, 'warning', error);
  }
}

export function formatTime(timeStr: string): string {
  try {
    if (!timeStr) return '';
    timeStr = timeStr.toLowerCase().trim();

    // Convert 24-hour format to 12-hour format without seconds
    const convertTo12Hour = (hours: number, minutes: number): string => {
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    // Handle 24-hour format (e.g., "14:30" or "23:45")
    if (timeStr.match(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return convertTo12Hour(hours, minutes);
    }

    // Handle 12-hour format (e.g., "2:30 pm")
    if (timeStr.match(/^\d{1,2}:\d{2}\s*[ap]m$/i)) {
      const [time, period] = timeStr.split(/\s+/);
      const [hour, minutes] = time.split(':').map(Number);
      const hours = period.toLowerCase() === 'pm' && hour !== 12 ? hour + 12 : hour;
      return convertTo12Hour(hours, minutes);
    }

    // Handle single hour in 24-hour format (e.g., "14" or "23")
    if (timeStr.match(/^([01]?[0-9]|2[0-3])$/)) {
      const hours = parseInt(timeStr);
      return convertTo12Hour(hours, 0);
    }

    return timeStr;
  } catch (error) {
    console.error('Error formatting time:', timeStr);
    return timeStr;
  }
};

export function GetAWSRegion(): string {
  const region = process.env.AWS_REGION || process.env.AWS_DEFAULT_REGION || 'us-east-1';
  if (!process.env.AWS_REGION && !process.env.AWS_DEFAULT_REGION) {
    const errorMessage = "AWS region is missing from environment variables, using default of us-east-1";
    console.error(errorMessage);
  }
  return region;
}

// SessionStorage key and max count for token-expired reload guard (prevents infinite loop)
const TOKEN_EXPIRED_RELOAD_KEY = 'TimePilotTokenExpiredReloadCount';
const TOKEN_EXPIRED_RELOAD_MAX = 3;
// Ensures only one 401 handler increments the reload count when many requests fail concurrently
let tokenExpiredReloadInProgress = false;

function getTokenExpiredReloadCount(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  const v = sessionStorage.getItem(TOKEN_EXPIRED_RELOAD_KEY);
  const n = parseInt(v ?? '0', 10);
  return Number.isNaN(n) ? 0 : n;
}

function incrementTokenExpiredReloadCount(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  const count = getTokenExpiredReloadCount() + 1;
  sessionStorage.setItem(TOKEN_EXPIRED_RELOAD_KEY, String(count));
  return count;
}

function clearTokenExpiredReloadCount(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(TOKEN_EXPIRED_RELOAD_KEY);
}

// Reload the page if the build ID mismatch
export function ReloadIfVersionMismatch(serverBuildId: string, accessToken: string): void {
  const key = 'TimePilotSkewReload';
  const clientBuildId = process.env.BUILD_ID;
  let user = 'unknown';
  try {
    user = getUserEmail(getTokenInfo(accessToken));
  } catch (error) {
    // no access token yet, so we can't get the user email
  }
  if (process.env.LOCAL_DEV !== 'true' && clientBuildId !== serverBuildId) {
    sessionStorage.setItem(key, 'true');
    trackEvent('version_mismatch_reload', {
      user: user,
    });
    window.location.reload();
  }
}

export function GetBuildId(): string | undefined {
  // TimePilot v0.1.18-2025-08-23T02:22:02.862Z
  if (process.env.BUILD_ID) {
    const versionName = process.env.BUILD_ID?.split('-')[0] || 'TimePilot';
    const buildDate = process.env.BUILD_ID?.split('-')?.slice(1)?.join('-')?.split('T')[0] || '';
    const buildTime = process.env.BUILD_ID?.split('-')?.slice(1)?.join('-')?.split('T')[1]?.split('.')[0] || '';
    return `${versionName} ${buildDate} ${buildTime}`;
  }
  return 'TimePilot';
}

// Use the Build Version Header to check the build version
export function CheckBuildVersionHeader(response: Response, accessToken: string): void {
  const serverBuildId = response?.headers?.get('x-app-version') || null;
  if (!!serverBuildId) {
    ReloadIfVersionMismatch(serverBuildId, accessToken);
  }
}

// an alias for fetch that also checks the build version header
export async function FetchWithVersionCheck(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const useIntercom = useIntercomFromCookieValue(getCookie('useIntercom'));
  let accessToken = ((options.headers as any)?.['Authorization'] || '').replace('Bearer ', '');
  if (!accessToken || accessToken.trim() === '') {
    try {
      accessToken = await getAccessToken();
    } catch (error) {
      try {
        accessToken = getCookie('accessToken');
      } catch {
        CaptureError('Error getting access token: ' + error, error as Error, useIntercom);
      }
    }
  }
  if (!options.signal) {
    options.signal = AbortSignal.timeout(90000);
  }
  if (!options.cache) {
    options.cache = 'default';
  }
  const newHeaders = !!(options.headers as any)?.['Authorization'] ? options.headers : {
    ...options.headers,
  };
  if (accessToken) {
    (newHeaders as any)['Authorization'] = `Bearer ${accessToken}`;
  }
  options.headers = newHeaders;
  try {
    const response = await fetch(url, options);
    if (response.ok) {
      CheckBuildVersionHeader(response, accessToken);
      // Token is valid; clear reload guard so future token expiry can trigger recovery again
      clearTokenExpiredReloadCount();
    }
    // CSM Cognito: when token has expired, recover so ALB can refresh or user re-auths.
    // Guard against infinite reload/redirect: if refresh keeps failing (e.g. Cognito/ALB edge case),
    // redirect to logout after TOKEN_EXPIRED_RELOAD_MAX attempts so the user can sign in again.
    // Only one concurrent 401 handler runs: multiple in-flight requests must not each increment the reload count.
    if (response.status === 401 && response.headers.get('X-Auth-Token-Expired') === 'true') {
      if (typeof window !== 'undefined') {
        if (tokenExpiredReloadInProgress) {
          return response;
        }
        tokenExpiredReloadInProgress = true;
        const count = incrementTokenExpiredReloadCount();
        if (count > TOKEN_EXPIRED_RELOAD_MAX) {
          trackEvent('token_expired_reload_limit_reached', {
            user: getUserEmail(getTokenInfo(accessToken)),
            count: count,
          });
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(TOKEN_EXPIRED_RELOAD_KEY);
          }
          window.location.href = '/logout-confirmation';
          return response;
        }
        const isCsmUser = getCookie('tai_csm_user') === '1';
        if (isCsmUser) {
          window.location.href = '/csm';
        } else {
          window.location.reload();
        }
      }
    }
    return response;
  } catch (error) {
    // do nothing for now
    return Promise.resolve(new Response('Error fetching with version check: ' + error, { status: 500 }));
  }
}

// Use the Build Version API to check the build version
export async function CheckBuildVersionViaApi() {
  const response = await FetchWithVersionCheck(`/api/version`, { method: 'HEAD', cache: 'no-store' });
}

export const getDownloadUrl = async (source: Source) => {
  const response = await FetchWithVersionCheck ('/api/protected/download', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(source),
  });
  const url = await response.json();
  return url;
};

export const handleDownload = async (sourceObject: SourceObject | Source, useIntercom: boolean = false) => {
  try {
    const source: Source = {
      index: 0,
      content: '',
      location: '',
      page: '',
      sourceFilePath: sourceObject.sourceUrl || sourceObject.sourceFilePath,
      truncatedFilePath: sourceObject.sourceUrl || sourceObject.sourceFilePath
    };

    const url = await getDownloadUrl(source);
    if (url.error) {
      const errorMessage = `Error downloading document: ${url.error}`;
      CaptureError(errorMessage, url.error as Error, useIntercom);
      alert(errorMessage);
      // TODO: Handle error (e.g., show an error message to the user)
    } else {
      window.open(url.signedUrl, '_blank');
    }
  } catch (error) {
    const errorMessage = `Error downloading document: ${error}`;
    CaptureError(errorMessage, error as Error, useIntercom);
    alert(errorMessage);
    // TODO: Handle error (e.g., show an error message to the user)
  }
};

export function removeMD5Hash(path: string): string {
  // Match pattern: single hyphen followed by 32 hexadecimal characters
  return path.replace(/-[a-f0-9]{32}/g, '');
}

/**
 * Remove a single leading `sharepoint` or `gdrive` segment (case-insensitive) when present.
 * Matches bulk paths like `bulk/DataSyncMT/<site-id>/sharepoint/<folder>/…` — only the
 * prefix immediately after the drive/site id, not a folder named `sharepoint` elsewhere.
 *
 * This fix was requested via:
 * https://tranquility-ai.atlassian.net/browse/TP-2556
 */
const STRIP_LEADING_PATH_SEGMENTS = new Set(['sharepoint', 'gdrive']);

export function stripUnwantedLeadingPathSegments(path: string): string {
  if (!path) return path;
  const segments = path.split('/');
  if (segments.length === 0) return path;
  if (STRIP_LEADING_PATH_SEGMENTS.has(segments[0].toLowerCase())) {
    return segments.slice(1).join('/');
  }
  return path;
}

/**
 * Scope rows from `/api/protected/cases/:caseId` are full `case_id` paths. For the Evidence Locker
 * folder list, show only the path under the current case: strip `caseId/` when present; otherwise
 * fall back to dropping the first segment (legacy tenant-only trim).
 */
export function folderPathForEvidenceLockerDisplay(scope: string, caseId: string): string {
  if (!scope) return scope;
  if (caseId && caseId !== '*') {
    const prefix = `${caseId}/`;
    if (scope.startsWith(prefix)) {
      return scope.slice(prefix.length);
    }
    if (scope.includes('/')) {
      return scope.split('/').slice(1).join('/');
    }
  }
  return scope;
}

// Safe URI decoding function that handles malformed URIs
export function safeDecodeURIComponent(str: string): string {
  try {
    if (!str.includes('%2F')) {
      return str;
    }
    return decodeURIComponent(str);
  } catch (e) {
    // If decoding fails, return the original string
    CaptureWarning(`Failed to decode URI component: ${str}`, e as Error, false);
    return str;
  }
}

// use regex instead of looping through SOURCE_TYPES
export function extractPathAfterBulk(objectUrl: string, removeMD5: boolean = false): Source {
  let truncatedPathWithParams;
  let sourceType = 'Unknown';
  // label all extracted sources as type textract
  if (objectUrl && objectUrl.trim() === '') {
    return { truncatedFilePath: undefined, sourceType: 'Unknown' };
  } else if (objectUrl && objectUrl.toLowerCase().includes('/bkb/slow/')) {
    truncatedPathWithParams = safeDecodeURIComponent(objectUrl.slice(objectUrl.toLowerCase().indexOf('/bkb/slow/') + '/bkb/slow/'.length));
    sourceType = 'Textract';
  } else if (objectUrl && objectUrl.trim() !== '') {
    // Create regex pattern for all source types
    const sourceTypesPattern = SOURCE_TYPES.join('|');
    const bulkRegex = new RegExp(`bulk/(${sourceTypesPattern})/`, 'i');
    const match = objectUrl.match(bulkRegex);

    if (match) {
      const rawCaptured = match[1];
      const matchedSourceType =
        SOURCE_TYPES.find((t) => t.toLowerCase() === rawCaptured.toLowerCase()) ?? rawCaptured;
      const bulkMarker = `bulk/${matchedSourceType}/`;
      const index = objectUrl.toLowerCase().indexOf(bulkMarker.toLowerCase());

      // After bulk/<type>/, some sources prefix a drive/site id, then `sharepoint/` and folders.
      // OneDrive and DataSync* use this shape, e.g.:
      // bulk/DataSyncMT/<site-id>/sharepoint/<folder>/file.docx
      //
      // This fix was requested via:
      // https://tranquility-ai.atlassian.net/browse/TP-2556
      const stripSharePointAfterDriveId = (t: string) => t === 'OneDrive' || t === 'DataSyncMT';

      switch (matchedSourceType) {
        case 'GoogleDrive':
        case 'OneDrive':
        case 'DataSyncMT':
          // Remove the drive / site id from the path as well
          const driveIdAndPath = objectUrl.slice(index + bulkMarker.length);
          const driveIdIndex = driveIdAndPath.indexOf('/');
          if (driveIdIndex !== -1) {
            let rest = driveIdAndPath.slice(driveIdIndex + 1);
            if (stripSharePointAfterDriveId(matchedSourceType)) {
              rest = stripUnwantedLeadingPathSegments(rest);
            }
            truncatedPathWithParams = rest;
            sourceType = matchedSourceType;
            break;
          }
          truncatedPathWithParams = driveIdAndPath;
          sourceType = matchedSourceType;
          break;
        default:
          truncatedPathWithParams = objectUrl.slice(index + bulkMarker.length);
          sourceType = matchedSourceType;
          break;
      }
    } else {
      // If we haven't found a path yet but the URL contains 'bulk/', extract everything after it
      const bulkMatch = objectUrl.toLowerCase().match(/bulk\//);
      if (bulkMatch) {
        truncatedPathWithParams = objectUrl.slice(objectUrl.toLowerCase().indexOf('bulk/') + 'bulk/'.length);
      }
    }
    truncatedPathWithParams = truncatedPathWithParams
      ? safeDecodeURIComponent(truncatedPathWithParams)
      : objectUrl;
  }

  const truncatedFilePath = truncatedPathWithParams ? (removeMD5 ? removeMD5Hash(truncatedPathWithParams.split('?')[0]) : truncatedPathWithParams.split('?')[0]) : truncatedPathWithParams;

  return { truncatedFilePath, sourceType };
}

export const isRestrictedFileType = (source: Source | SourceObject | MediaObject): boolean => {
  const sourceIn: any = source;
  const url = sourceIn.sourceUrl || sourceIn.mediaUrl || sourceIn.mediaSrc || sourceIn.sourceFilePath;
  const extension = (url.split(/[#?]/)[0].split('.').pop() || '').trim().toLowerCase();
  const extension2 = (url.split('.')?.at(-1) || '').trim().toLowerCase();
  return RestrictedSourceTypes.includes(extension) || RestrictedSourceTypes.includes(extension2);
}

// do not use as an export, but use as a part of the getMediaType function below
const guessMediaType = (
  source: Source | SourceObject | MediaObject
): MediaType => {
  let declaredMediaType = 'unknown';
  let sourceIn: any = source;
  if (sourceIn.previewFilePath && sourceIn.previewFilePath !== '') {
    const previewExtension = sourceIn.previewFilePath.split(/[#?]/)[0].split('.').pop()?.trim().toLowerCase();
    if (FileTypes[previewExtension as keyof typeof FileTypes]) {
      const mediaType = FileTypes[previewExtension as keyof typeof FileTypes] as MediaType;
      return mediaType || 'unknown' as MediaType;
    }
  }
  if (sourceIn.sourceType && sourceIn.sourceType !== '' && !Array.isArray(sourceIn.sourceType)) {
    declaredMediaType = sourceIn.sourceType as MediaType;
  } else if (sourceIn.mediaType && sourceIn.mediaType !== '') {
    declaredMediaType = sourceIn.mediaType as MediaType;
  }
  if (!sourceIn.sourceUrl && !sourceIn.mediaUrl && !sourceIn.mediaSrc && !sourceIn.sourceFilePath && !sourceIn.source_s3_path) return declaredMediaType as MediaType;
  const url = sourceIn.sourceUrl || sourceIn.mediaUrl || sourceIn.mediaSrc || sourceIn.sourceFilePath || sourceIn.source_s3_path;
  const extension = (url.split(/[#?]/)[0].split('.').pop() || '').trim().toLowerCase();
  // the following is for s3 urls that have # in the actual filename
  const extension2 = (url.split('.')?.at(-1) || '').trim().toLowerCase();
  if (FileTypes[extension as keyof typeof FileTypes]) {
    return FileTypes[extension as keyof typeof FileTypes] as MediaType;
  } else if (FileTypes[extension2 as keyof typeof FileTypes]) {
    return FileTypes[extension2 as keyof typeof FileTypes] as MediaType;
  } else if (declaredMediaType !== 'unknown') {
    return declaredMediaType as MediaType;
  } else {
    return 'unknown' as MediaType;
  }
}

export const checkSourceContentType = (sourceInfo: SourceInfo): MediaType | undefined => {
  if (sourceInfo.sourceContentType) {
    // Strip parameters (e.g. "; charset=utf-8") so "text/html; charset=utf-8" matches type-specific cases
    const contentType = sourceInfo.sourceContentType.split(';')[0].trim().toLowerCase();
    const mainContentType = (contentType.split('/')[0] || 'unknown').toLowerCase();
    switch (contentType) {
      case 'text/html':
        return 'html';
      case 'text/plain':
        return 'text';
      case 'text/xml':
        return 'text';
      case 'text/markdown':
        return 'markdown';
      case 'application/xml':
        return 'text';
      case 'application/pdf':
        return 'pdf';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'spreadsheet';
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        return undefined;
      default:
        switch (mainContentType) {
          case 'image':
            return 'image';
          case 'video':
            return 'video';
          case 'audio':
            return 'audio';
          case 'text':
            return 'text';
          default:
            return undefined;
        }
    }
  }
  return undefined;
}

export const getMediaType = (source: Source | MediaObject, sourceInfo?: SourceInfo | null | undefined): MediaType => {
  const mediaTypeFromExtension = guessMediaType(source);
  let thisMediaType: MediaType | undefined = mediaTypeFromExtension;
  const sourceInfoToUse = !!sourceInfo ? sourceInfo : source.sourceInfo;
  if (!!sourceInfo || !!source.sourceInfo) {
    let foundMediaType = false;
    if (sourceInfoToUse?.isImage) {
      thisMediaType = 'image';
      foundMediaType = true;
    } else if (sourceInfoToUse?.isAudioVideo) {
      if (sourceInfoToUse.contentCategories?.includes('VIDEO') || sourceInfoToUse.sourceContentType?.includes('video')) {
        thisMediaType = 'video';
        foundMediaType = true;
      } else {
        thisMediaType = 'audio';
        foundMediaType = true;
      }
    } else if (sourceInfoToUse?.contentCategories && sourceInfoToUse?.contentCategories.length > 0) {
      for (const category of sourceInfoToUse?.contentCategories || []) {
        const mediaTypeFromSourceInfo: MediaType = category.toLowerCase() as MediaType;
        if (mediaTypeFromSourceInfo && UsableMediaTypes.includes(mediaTypeFromSourceInfo)) {
          thisMediaType = mediaTypeFromSourceInfo;
          foundMediaType = true;
          break;
        }
      }
    }
    if (!foundMediaType && sourceInfoToUse?.sourceContentType) {
      const sourceContentTypeMediaType = checkSourceContentType(sourceInfoToUse);
      if (sourceContentTypeMediaType !== 'text' && sourceContentTypeMediaType !== 'unknown' && mediaTypeFromExtension === 'unknown') {
        thisMediaType = sourceContentTypeMediaType;
      }
    }
    if (!foundMediaType && sourceInfoToUse?.sourceFileType && thisMediaType === 'unknown') {
      const ft = sourceInfoToUse.sourceFileType.toLowerCase();
      const mappedType = FileTypes[ft as keyof typeof FileTypes] as MediaType | undefined;
      if (mappedType && mappedType !== 'unknown') {
        thisMediaType = mappedType;
      }
    }
  }
  return thisMediaType || mediaTypeFromExtension || 'unknown' as MediaType;
}

// transcript times look like this in the content \"start\": \"4:27\", \"end\": \"4:29\"
const startRegex = /"start":\s*"((\d+:){0,1}(\d+:\d+))"/g;
const endRegex = /"end":\s*"((\d+:){0,1}(\d+:\d+))"/g;

export const getSecondsFromTranscript = (content: string): number => {
  let startSeconds = 0;
  const startIndexStringMatches = content.match(startRegex);
  const startIndexString = startIndexStringMatches?.[0] || '"start": "0:0:0"';
  const startIndexParts = startIndexString.split(':').slice(1).join(':')?.replaceAll(/[^0-9:]/g, '')?.split(':') || [];
  if (startIndexParts.length === 1) {
    startSeconds = parseInt(startIndexParts[0], 10);
  } else if (startIndexParts.length === 2) {
    startSeconds = parseInt(startIndexParts[0], 10) * 60 + parseInt(startIndexParts[1], 10);
  } else if (startIndexParts.length === 3) {
    startSeconds = parseInt(startIndexParts[0], 10) * 3600 + parseInt(startIndexParts[1], 10) * 60 + parseInt(startIndexParts[2], 10);
  }

  let endSeconds = 0;
  const endIndexStringMatches = content.match(endRegex);
  const endIndexString = endIndexStringMatches?.[endIndexStringMatches.length - 1] || '"end": "0:0:0"';
  const endIndexParts = endIndexString.split(':').slice(1).join(':')?.replaceAll(/[^0-9:]/g, '')?.split(':') || [];
  if (endIndexParts.length === 1) {
    endSeconds = parseInt(endIndexParts[0], 10);
  } else if (endIndexParts.length === 2) {
    endSeconds = parseInt(endIndexParts[0], 10) * 60 + parseInt(endIndexParts[1], 10);
  } else if (endIndexParts.length === 3) {
    endSeconds = parseInt(endIndexParts[0], 10) * 3600 + parseInt(endIndexParts[1], 10) * 60 + parseInt(endIndexParts[2], 10);
  }
  if (endSeconds > startSeconds) {
    return endSeconds;
  }
  return startSeconds;
}

// deduplicate all source within an answer
export const getTimeSaved = (source: SimpleSource): number => {
  let pagesRead = 0;
  let extraPages = 0;
  let secondsSaved = 0;
  const avgWordsPerMinute = 238;
  const avgWordsPerPage = 466; // 233 words per page but 2x since this is technical material

  const mediaType: any = getMediaType(source as unknown as Source);
  switch (mediaType) {
    case 'image':
      pagesRead += 5;
      break;
    case 'audio':
      pagesRead = 15;
      if (source.location && source.location?.includes('/bkb/slow/transcribe/')) {
        if (source.content) {
          secondsSaved = getSecondsFromTranscript(source.content);
        }
        extraPages += 5;
      }
      break;
    case 'video':
      pagesRead = 15;
      if (source.location && source.location?.includes('/bkb/slow/transcribe/')) {
        if (source.content) {
          secondsSaved = getSecondsFromTranscript(source.content);
        }
        extraPages += 5;
      }
      break;
    case 'pdf':
      pagesRead = source.page || 2;
      break;
    case 'docx':
      pagesRead = source.page || 2;
      break;
    case 'document':
      pagesRead = source.page || 1;
      break;
    case 'spreadsheet':
      pagesRead = source.page || 1;
      break;
    case 'presentation':
      pagesRead = source.page || 1;
      break;
    case 'html':
      pagesRead = source.page || 20;
      break;
    case 'text':
      let filename = source.location.split('/').pop();
      filename = filename?.split('.')[0];
      pagesRead = parseInt(filename || '1', 10) || 1;
      break;
    default:
      pagesRead = 1;
      break;
  }

  const timeSaved = (pagesRead * avgWordsPerPage) / avgWordsPerMinute;
  const extraTimeSaved = (extraPages * avgWordsPerPage) / avgWordsPerMinute;
  const minutesSaved = secondsSaved / 60;

  const additionalTimeSaved = minutesSaved > extraTimeSaved ? minutesSaved : extraTimeSaved;

  const totalTimeSaved = (timeSaved + additionalTimeSaved) * 1.1;

  return totalTimeSaved;
}

/**
 * Converts a caseId to a display name.
 * Handles '*' → 'All Cases' and removes tenant prefix if present.
 * @param caseId - The case ID (may include tenant prefix like 'tenant/caseName' or be '*')
 * @returns The display name for the case
 */
export const getCaseDisplayName = (caseId: string | undefined | null): string => {
  if (!caseId) return '';
  if (caseId === '*') return 'All Cases';
  // Remove tenant prefix if present (format: tenant/caseName)
  const parts = caseId.split('/');
  return parts.length > 1 ? parts.slice(1).join('/') : caseId;
};

/**
 * Formats timeline text by replacing '*' with 'All Cases'.
 * Used for timeline titles and descriptions that may contain '*'.
 * @param text - The text to format (may be null/undefined)
 * @returns The formatted text with '*' replaced by 'All Cases'
 */
export const formatTimelineDisplayText = (text: string | null | undefined): string => {
  if (!text) return text || '';
  return text.replace(/\*/g, 'All Cases');
};
